import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTION_TYPES, useCustomPageCreateEditDispatch, useCustomPageCreateEditState } from './custom-page-create-edit-context';
import CustomPageAppService from '../../../../application/platform/apm/custom-page/custom-page-app-service';
import { ICustomPageResponse } from '../../../../model/apm/custom-page/response/custom-page-response';
import { ICustomPageSectionResponse } from '../../../../model/apm/custom-page/response/custom-page-section-response';
import { ICustomPageSectionItemResponse } from '../../../../model/apm/custom-page/response/custom-page-section-item-response';
import { ICustomPageSectionItemAddEditRequest } from '../../../../model/apm/custom-page/request/custom-page-section-item-add-edit-request';
import { TipoSecaoPaginaCustomizada } from '../../../../model/apm/apm-enums';
import { reorderOptimistic } from '../../components/reorder-utils';
import { buildSectionRequest, pickFields } from '../../components/field-config';
import { removeById, upsertById } from '../../../../utils/list-utils';
import { useActionRunner } from '../../../../hooks/use-action-runner';
import { CUSTOM_PAGE_SECTION_CONFIG } from '../components/custom-page-section-config';

export const useCustomPageCreateEditController = () => {
    const navigate = useNavigate();
    const dispatch = useCustomPageCreateEditDispatch();
    const state = useCustomPageCreateEditState();
    const { isSaving, runSaving, runWithLoading, run, showSuccess, confirmDelete } = useActionRunner();
    const [isUploadingCover, setIsUploadingCover] = useState(false);
    const pageId = state.customPageId ? Number(state.customPageId) : undefined;

    useEffect(() => {
        if (!pageId) return;
        runWithLoading('Não foi possível carregar a página', async () => {
            dispatch({ type: ACTION_TYPES.setPage, payload: await CustomPageAppService.getById(pageId) });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageId]);

    // Dados da página
    function setPage(page: ICustomPageResponse) {
        // Respostas sem seções mantêm as já carregadas
        dispatch({ type: ACTION_TYPES.setPage, payload: { ...page, sections: page.sections ?? state.sections } });
    }

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        dispatch({ type: ACTION_TYPES.updatePageRequest, payload: { [name]: value } });
    }

    function handleCoverChange(coverImage?: string) {
        dispatch({ type: ACTION_TYPES.updatePageRequest, payload: { coverImage } });
    }

    function handleSave() {
        const title = state.pageRequest.title?.trim();
        if (!title) {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            return;
        }
        // O endereço é gerado pela API na criação; na edição reenvia o atual para a URL não mudar com o título
        const request = { ...state.pageRequest, title, slug: pageId ? state.page?.slug : undefined };
        return runSaving('Não foi possível salvar a página', async () => {
            if (pageId) {
                setPage(await CustomPageAppService.edit(pageId, request));
                showSuccess('Página atualizada com sucesso!');
                return;
            }
            const page = await CustomPageAppService.add(request);
            showSuccess('Página criada! Agora adicione as seções do conteúdo.');
            navigate(`/paginas/editar/${page.id}`, { replace: true });
        });
    }

    function handleCancel() {
        navigate('/paginas');
    }

    function handleTogglePublication() {
        if (!pageId || !state.page) return;
        const isPublished = !state.page.isPublished;
        runSaving('Não foi possível alterar a publicação da página', async () => {
            setPage(await CustomPageAppService.changePublication(pageId, isPublished));
            showSuccess(isPublished ? 'Página publicada com sucesso!' : 'Página despublicada. Ela não aparece mais no portal.');
        });
    }

    // Seções
    function sectionRequest(type: TipoSecaoPaginaCustomizada, value: Record<string, any>) {
        return buildSectionRequest(CUSTOM_PAGE_SECTION_CONFIG[type].fields, value);
    }

    function setSections(sections: ICustomPageSectionResponse[]) {
        dispatch({ type: ACTION_TYPES.setSections, payload: sections });
    }

    // A resposta da edição pode vir sem itens; mantém os já carregados
    function upsertSection(saved: ICustomPageSectionResponse) {
        const current = state.sections.find((s) => s.id === saved.id);
        setSections(upsertById(state.sections, { ...saved, items: saved.items ?? current?.items }));
    }

    function openSectionDrawer(section?: ICustomPageSectionResponse) {
        dispatch({ type: ACTION_TYPES.openSectionDrawer, payload: section });
    }

    function closeSectionDrawer() {
        dispatch({ type: ACTION_TYPES.closeSectionDrawer });
    }

    function handleSaveSection(value: Record<string, any>) {
        if (!pageId) return;
        const editing = state.sectionDrawer.section;
        const type: TipoSecaoPaginaCustomizada = editing ? editing.type : value.type;
        const request = sectionRequest(type, value);
        return runSaving('Não foi possível salvar a seção', async () => {
            upsertSection(await (editing
                ? CustomPageAppService.editSection(pageId, editing.id, request)
                : CustomPageAppService.addSection(pageId, { ...request, type })));
            showSuccess(editing ? 'Seção atualizada com sucesso!' : 'Seção adicionada com sucesso!');
            closeSectionDrawer();
        });
    }

    function handleToggleSectionVisibility(section: ICustomPageSectionResponse) {
        if (!pageId) return;
        run('Não foi possível alterar a visibilidade da seção', async () => {
            upsertSection(await CustomPageAppService.editSection(pageId, section.id, sectionRequest(section.type, { ...section, isVisible: !section.isVisible })));
            showSuccess(section.isVisible ? 'Seção ocultada na página.' : 'Seção exibida na página.');
        });
    }

    function handleDeleteSection(section: ICustomPageSectionResponse) {
        if (!pageId) return;
        confirmDelete('Tem certeza que deseja excluir esta seção e todos os seus itens?', () => run('Não foi possível excluir a seção', async () => {
            await CustomPageAppService.removeSection(pageId, section.id);
            setSections(removeById(state.sections, section.id));
            showSuccess('Seção excluída com sucesso!');
        }));
    }

    function handleReorderSections(next: ICustomPageSectionResponse[]) {
        if (!pageId) return;
        run('Não foi possível reordenar as seções', () => reorderOptimistic(
            state.sections, next, (s) => s.id, setSections,
            (ids) => CustomPageAppService.reorderSections(pageId, ids),
        ));
    }

    // Itens
    function openItemDrawer(section: ICustomPageSectionResponse, item?: ICustomPageSectionItemResponse) {
        dispatch({ type: ACTION_TYPES.openItemDrawer, payload: { section, item } });
    }

    function closeItemDrawer() {
        dispatch({ type: ACTION_TYPES.closeItemDrawer });
    }

    function sectionItems(sectionId: number) {
        return state.sections.find((s) => s.id === sectionId)?.items || [];
    }

    function setSectionItems(sectionId: number, items: ICustomPageSectionItemResponse[]) {
        dispatch({ type: ACTION_TYPES.setSectionItems, payload: { sectionId, items } });
    }

    function handleSaveItem(value: Record<string, any>) {
        const { section, item: editing } = state.itemDrawer;
        if (!section) return;
        const itemFields = CUSTOM_PAGE_SECTION_CONFIG[section.type].itemFields || {};
        const request = pickFields(itemFields, value) as ICustomPageSectionItemAddEditRequest;
        return runSaving('Não foi possível salvar o item', async () => {
            const saved = await (editing
                ? CustomPageAppService.editItem(section.id, editing.id, request)
                : CustomPageAppService.addItem(section.id, request));
            setSectionItems(section.id, upsertById(sectionItems(section.id), saved));
            showSuccess(editing ? 'Item atualizado com sucesso!' : 'Item adicionado com sucesso!');
            closeItemDrawer();
        });
    }

    function handleDeleteItem(section: ICustomPageSectionResponse, item: ICustomPageSectionItemResponse) {
        const message = item.title?.trim() ? `Tem certeza que deseja excluir "${item.title}"?` : 'Tem certeza que deseja excluir este item?';
        confirmDelete(message, () => run('Não foi possível excluir o item', async () => {
            await CustomPageAppService.removeItem(section.id, item.id);
            setSectionItems(section.id, removeById(sectionItems(section.id), item.id));
            showSuccess('Item excluído com sucesso!');
        }));
    }

    function handleReorderItems(section: ICustomPageSectionResponse, next: ICustomPageSectionItemResponse[]) {
        run('Não foi possível reordenar os itens', () => reorderOptimistic(
            section.items || [], next, (i) => i.id,
            (items) => setSectionItems(section.id, items),
            (ids) => CustomPageAppService.reorderItems(section.id, ids),
        ));
    }

    return {
        isSaving, isUploadingCover, setIsUploadingCover,
        handleOnChange, handleCoverChange, handleSave, handleCancel, handleTogglePublication,
        openSectionDrawer, closeSectionDrawer, handleSaveSection, handleToggleSectionVisibility, handleDeleteSection, handleReorderSections,
        openItemDrawer, closeItemDrawer, handleSaveItem, handleDeleteItem, handleReorderItems,
    };
};
